import { parseFieldKey } from '@mastra/core/utils';
import type {
  BasicOperator,
  NumericOperator,
  ArrayOperator,
  ElementOperator,
  LogicalOperator,
  RegexOperator,
  VectorFilter,
} from '@mastra/core/vector/filter';
import type { PGVectorFilter } from './filter';

type OperatorType =
  | BasicOperator
  | NumericOperator
  | ArrayOperator
  | ElementOperator
  | LogicalOperator
  | '$contains'
  | Exclude<RegexOperator, '$options'>
  | '$size';

type FilterOperator = {
  sql: string;
  needsValue: boolean;
  transformValue?: () => any;
};

type OperatorFn = (key: string, paramIndex: number, value?: any) => FilterOperator;

const getTextExtractExpr = (key: string) => {
  const jsonPathKey = parseJsonPathKey(key);
  if (!key.includes('.')) {
    return `metadata->>'${jsonPathKey}'`;
  }
  return `metadata#>>'{${jsonPathKey}}'`;
};

const getJsonExtractExpr = (key: string) => {
  const jsonPathKey = parseJsonPathKey(key);
  if (!key.includes('.')) {
    return `metadata->'${jsonPathKey}'`;
  }
  return `metadata#>'{${jsonPathKey}}'`;
};

const createBasicOperator = (symbol: string) => {
  return (key: string, paramIndex: number) => {
    const textExtract = getTextExtractExpr(key);
    return {
      sql: `CASE
        WHEN $${paramIndex}::text IS NULL THEN ${textExtract} IS ${symbol === '=' ? '' : 'NOT'} NULL
        ELSE ${textExtract} ${symbol} $${paramIndex}::text
      END`,
      needsValue: true,
    };
  };
};

const createNumericOperator = (symbol: string) => {
  return (key: string, paramIndex: number, value?: any) => {
    const textExtract = getTextExtractExpr(key);
    const jsonExtract = getJsonExtractExpr(key);

    // Check if the value is a number or can be parsed as a number
    const isNumeric =
      typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '');

    // Use numeric comparison for numbers, text comparison for strings/dates
    if (isNumeric) {
      // JSONB metadata is schemaless, so a candidate row may hold a non-numeric
      // value at this path (e.g. { price: 'N/A' }). Casting the column to ::numeric
      // unconditionally makes Postgres raise 22P02 and fail the ENTIRE query instead
      // of that row simply not matching. Guard the cast with jsonb_typeof so only
      // number-typed rows are compared and everything else is excluded, mirroring the
      // $size/$in pattern already used in this file and MongoDB-style range semantics.
      return {
        sql: `(CASE WHEN jsonb_typeof(${jsonExtract}) = 'number' THEN (${textExtract})::numeric ${symbol} $${paramIndex}::numeric ELSE NULL END)`,
        needsValue: true,
      };
    } else {
      // Use text comparison for strings (including ISO 8601 dates which sort correctly)
      return {
        sql: `${textExtract} ${symbol} $${paramIndex}::text`,
        needsValue: true,
      };
    }
  };
};

function buildElemMatchConditions(value: any, paramIndex: number): { sql: string; values: any[] } {
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('$elemMatch requires an object with conditions');
  }

  const conditions: string[] = [];
  const values: any[] = [];

  Object.entries(value).forEach(([field, val]) => {
    const nextParamIndex = paramIndex + values.length;

    let paramOperator;
    let paramKey;
    let paramValue;

    if (field.startsWith('$')) {
      paramOperator = field;
      paramKey = '';
      paramValue = val;
    } else if (typeof val === 'object' && !Array.isArray(val)) {
      const [op, opValue] = Object.entries(val || {})[0] || [];
      paramOperator = op;
      paramKey = field;
      paramValue = opValue;
    } else {
      paramOperator = '$eq';
      paramKey = field;
      paramValue = val;
    }

    const operatorFn = FILTER_OPERATORS[paramOperator as OperatorType];
    if (!operatorFn) {
      throw new Error(`Invalid operator: ${paramOperator}`);
    }
    const result = operatorFn(paramKey, nextParamIndex, paramValue);

    // Rewrite every column reference to the per-element alias. Order matters:
    // replace the longer `metadata#>>` / `metadata->>` tokens before `metadata#>` / `metadata->` so the latter
    // doesn't partially match the former (e.g. the jsonb_typeof guard emitted by
    // the numeric operators uses the single-arrow `metadata->` / `metadata#>` form).
    const sql = result.sql
      .replaceAll('metadata->>', 'elem->>')
      .replaceAll('metadata->', 'elem->')
      .replaceAll('metadata#>>', 'elem#>>')
      .replaceAll('metadata#>', 'elem#>');
    conditions.push(sql);
    if (result.needsValue) {
      values.push(paramValue);
    }
  });

  return {
    sql: conditions.join(' AND '),
    values,
  };
}

// Define all filter operators
const FILTER_OPERATORS: Record<OperatorType, OperatorFn> = {
  $eq: createBasicOperator('='),
  $ne: createBasicOperator('!='),
  $gt: createNumericOperator('>'),
  $gte: createNumericOperator('>='),
  $lt: createNumericOperator('<'),
  $lte: createNumericOperator('<='),

  // Array Operators
  $in: (key, paramIndex) => {
    const textExtract = getTextExtractExpr(key);
    const jsonExtract = getJsonExtractExpr(key);
    return {
      sql: `(
        CASE
          WHEN jsonb_typeof(${jsonExtract}) = 'array' THEN
            EXISTS (
              SELECT 1 FROM jsonb_array_elements_text(${jsonExtract}) as elem
              WHERE elem = ANY($${paramIndex}::text[])
            )
          ELSE ${textExtract} = ANY($${paramIndex}::text[])
        END
      )`,
      needsValue: true,
    };
  },
  $nin: (key, paramIndex) => {
    const textExtract = getTextExtractExpr(key);
    const jsonExtract = getJsonExtractExpr(key);
    return {
      sql: `(
        CASE
          WHEN jsonb_typeof(${jsonExtract}) = 'array' THEN
            NOT EXISTS (
              SELECT 1 FROM jsonb_array_elements_text(${jsonExtract}) as elem
              WHERE elem = ANY($${paramIndex}::text[])
            )
          ELSE ${textExtract} != ALL($${paramIndex}::text[])
        END
      )`,
      needsValue: true,
    };
  },
  $all: (key, paramIndex) => {
    const jsonExtract = getJsonExtractExpr(key);
    return {
      sql: `CASE WHEN array_length($${paramIndex}::text[], 1) IS NULL THEN false
            ELSE (${jsonExtract})::jsonb ?& $${paramIndex}::text[] END`,
      needsValue: true,
    };
  },
  $elemMatch: (key: string, paramIndex: number, value: any): FilterOperator => {
    const { sql, values } = buildElemMatchConditions(value, paramIndex);
    const jsonExtract = getJsonExtractExpr(key);
    return {
      sql: `(
        CASE
          WHEN jsonb_typeof(${jsonExtract}) = 'array' THEN
            EXISTS (
              SELECT 1
              FROM jsonb_array_elements(${jsonExtract}) as elem
              WHERE ${sql}
            )
          ELSE FALSE
        END
      )`,
      needsValue: true,
      transformValue: () => values,
    };
  },
  // Element Operators
  $exists: (key, paramIndex, value) => {
    const jsonPathKey = parseJsonPathKey(key);
    // If value is false, check that the key does NOT exist
    if (value === false) {
      return {
        sql: `NOT (metadata ? '${jsonPathKey}')`,
        needsValue: false,
      };
    }
    // Otherwise (true or truthy), check that the key exists
    return {
      sql: `metadata ? '${jsonPathKey}'`,
      needsValue: false,
    };
  },

  // Logical Operators
  $and: key => ({ sql: `(${key})`, needsValue: false }),
  $or: key => ({ sql: `(${key})`, needsValue: false }),
  $not: key => ({ sql: `(${key})`, needsValue: false }),
  $nor: key => ({ sql: `NOT (${key})`, needsValue: false }),

  // Regex Operators
  $regex: (key, paramIndex) => {
    const textExtract = getTextExtractExpr(key);
    return {
      sql: `${textExtract} ~ $${paramIndex}`,
      needsValue: true,
    };
  },

  $contains: (key, paramIndex, value: any) => {
    const textExtract = getTextExtractExpr(key);
    const jsonExtract = getJsonExtractExpr(key);
    let sql;
    if (Array.isArray(value)) {
      sql = `(${jsonExtract}) ?& $${paramIndex}`;
    } else if (typeof value === 'string') {
      sql = `${textExtract} ILIKE '%' || $${paramIndex} || '%' ESCAPE '\\'`;
    } else {
      sql = `${textExtract} = $${paramIndex}`;
    }
    return {
      sql,
      needsValue: true,
      transformValue: () =>
        Array.isArray(value) ? value.map(String) : typeof value === 'string' ? escapeLikePattern(value) : value,
    };
  },
  /**
   * $objectContains: Postgres-only operator for true JSONB object containment.
   * Usage: { field: { $objectContains: { ...subobject } } }
   */
  // $objectContains: (key, paramIndex) => ({
  //   sql: `metadata @> $${paramIndex}::jsonb`,
  //   needsValue: true,
  //   transformValue: value => {
  //     const parts = key.split('.');
  //     return JSON.stringify(parts.reduceRight((value, key) => ({ [key]: value }), value));
  //   },
  // }),
  $size: (key: string, paramIndex: number) => {
    const jsonExtract = getJsonExtractExpr(key);
    return {
      sql: `(
      CASE
        WHEN jsonb_typeof(${jsonExtract}) = 'array' THEN
          jsonb_array_length(${jsonExtract}) = $${paramIndex}
        ELSE FALSE
      END
    )`,
      needsValue: true,
    };
  },
};

interface FilterResult {
  sql: string;
  values: any[];
}

const parseJsonPathKey = (key: string) => {
  const parsedKey = key !== '' ? parseFieldKey(key) : '';
  return parsedKey.replace(/\./g, ',');
};

function escapeLikePattern(str: string): string {
  return str.replace(/([%_\\])/g, '\\$1');
}

/**
 * Build a filter query for DELETE operations (no minScore/topK parameters)
 */
export function buildDeleteFilterQuery(filter: PGVectorFilter): FilterResult {
  const values: any[] = [];

  function buildCondition(key: string, value: any, parentPath: string): string {
    // Handle logical operators ($and/$or)
    if (['$and', '$or', '$not', '$nor'].includes(key)) {
      return handleLogicalOperator(key as '$and' | '$or' | '$not' | '$nor', value, parentPath);
    }

    // If condition is not a FilterCondition object, assume it's an equality check
    if (!value || typeof value !== 'object') {
      values.push(value);
      return `${getTextExtractExpr(key)} = $${values.length}`;
    }

    // Handle operator conditions
    const entries = Object.entries(value);

    // If multiple operators on same field (e.g., { $gte: 20, $lte: 80 }), combine with AND
    if (entries.length > 1) {
      const conditions = entries.map(([operator, operatorValue]) => {
        // Special handling for nested $not
        if (operator === '$not') {
          const nestedEntries = Object.entries(operatorValue as Record<string, unknown>);
          const nestedConditions = nestedEntries
            .map(([nestedOp, nestedValue]) => {
              if (!FILTER_OPERATORS[nestedOp as OperatorType]) {
                throw new Error(`Invalid operator in $not condition: ${nestedOp}`);
              }
              const operatorFn = FILTER_OPERATORS[nestedOp as OperatorType]!;
              const operatorResult = operatorFn(key, values.length + 1, nestedValue);
              if (operatorResult.needsValue) {
                const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : nestedValue;
                if (Array.isArray(transformedValue) && nestedOp === '$elemMatch') {
                  values.push(...transformedValue);
                } else {
                  values.push(transformedValue);
                }
              }
              return operatorResult.sql;
            })
            .join(' AND ');
          return `NOT (${nestedConditions})`;
        }

        if (!FILTER_OPERATORS[operator as OperatorType]) {
          throw new Error(`Invalid operator: ${operator}`);
        }
        const operatorFn = FILTER_OPERATORS[operator as OperatorType]!;
        const operatorResult = operatorFn(key, values.length + 1, operatorValue);
        if (operatorResult.needsValue) {
          const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : operatorValue;
          if (Array.isArray(transformedValue) && operator === '$elemMatch') {
            values.push(...transformedValue);
          } else {
            values.push(transformedValue);
          }
        }
        return operatorResult.sql;
      });
      return conditions.join(' AND ');
    }

    // Single operator case
    const [[operator, operatorValue] = []] = entries;

    // Special handling for nested $not
    if (operator === '$not') {
      const nestedEntries = Object.entries(operatorValue as Record<string, unknown>);
      const conditions = nestedEntries
        .map(([nestedOp, nestedValue]) => {
          if (!FILTER_OPERATORS[nestedOp as OperatorType]) {
            throw new Error(`Invalid operator in $not condition: ${nestedOp}`);
          }
          const operatorFn = FILTER_OPERATORS[nestedOp as OperatorType]!;
          const operatorResult = operatorFn(key, values.length + 1, nestedValue);
          if (operatorResult.needsValue) {
            const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : nestedValue;
            if (Array.isArray(transformedValue) && nestedOp === '$elemMatch') {
              values.push(...transformedValue);
            } else {
              values.push(transformedValue);
            }
          }
          return operatorResult.sql;
        })
        .join(' AND ');

      return `NOT (${conditions})`;
    }
    const operatorFn = FILTER_OPERATORS[operator as OperatorType]!;
    const operatorResult = operatorFn(key, values.length + 1, operatorValue);
    if (operatorResult.needsValue) {
      const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : operatorValue;
      if (Array.isArray(transformedValue) && operator === '$elemMatch') {
        values.push(...transformedValue);
      } else {
        values.push(transformedValue);
      }
    }
    return operatorResult.sql;
  }

  function handleLogicalOperator(
    key: '$and' | '$or' | '$not' | '$nor',
    value: VectorFilter[],
    parentPath: string,
  ): string {
    if (key === '$not') {
      // For top-level $not
      const entries = Object.entries(value);
      const conditions = entries
        .map(([fieldKey, fieldValue]) => buildCondition(fieldKey, fieldValue, key))
        .join(' AND ');
      return `NOT (${conditions})`;
    }

    // Handle empty conditions
    if (!value || value.length === 0) {
      switch (key) {
        case '$and':
        case '$nor':
          return 'true'; // Empty $and/$nor match everything
        case '$or':
          return 'false'; // Empty $or matches nothing
        default:
          return 'true';
      }
    }

    const joinOperator = key === '$or' || key === '$nor' ? 'OR' : 'AND';
    const conditions = value.map((f: VectorFilter) => {
      const entries = Object.entries(f || {});
      if (entries.length === 0) return '';

      const [firstKey, firstValue] = entries[0] || [];
      if (['$and', '$or', '$not', '$nor'].includes(firstKey as string)) {
        return buildCondition(firstKey as string, firstValue, parentPath);
      }
      return entries.map(([k, v]) => buildCondition(k, v, parentPath)).join(` ${joinOperator} `);
    });

    const joined = conditions.join(` ${joinOperator} `);
    const operatorFn = FILTER_OPERATORS[key]!;
    return operatorFn(joined, 0, value).sql;
  }

  if (!filter) {
    return { sql: '', values };
  }

  const conditions = Object.entries(filter)
    .map(([key, value]) => buildCondition(key, value, ''))
    .filter(Boolean)
    .join(' AND ');

  return { sql: conditions ? `WHERE ${conditions}` : '', values };
}

export function buildFilterQuery(filter: PGVectorFilter, minScore: number, topK: number): FilterResult {
  const values = [minScore, topK];

  function buildCondition(key: string, value: any, parentPath: string): string {
    // Handle logical operators ($and/$or)
    if (['$and', '$or', '$not', '$nor'].includes(key)) {
      return handleLogicalOperator(key as '$and' | '$or' | '$not' | '$nor', value, parentPath);
    }

    // If condition is not a FilterCondition object, assume it's an equality check
    if (!value || typeof value !== 'object') {
      values.push(value);
      return `${getTextExtractExpr(key)} = $${values.length}`;
    }

    // Handle operator conditions
    const entries = Object.entries(value);

    // If multiple operators on same field (e.g., { $gte: 20, $lte: 80 }), combine with AND
    if (entries.length > 1) {
      const conditions = entries.map(([operator, operatorValue]) => {
        // Special handling for nested $not
        if (operator === '$not') {
          const nestedEntries = Object.entries(operatorValue as Record<string, unknown>);
          const nestedConditions = nestedEntries
            .map(([nestedOp, nestedValue]) => {
              if (!FILTER_OPERATORS[nestedOp as OperatorType]) {
                throw new Error(`Invalid operator in $not condition: ${nestedOp}`);
              }
              const operatorFn = FILTER_OPERATORS[nestedOp as OperatorType]!;
              const operatorResult = operatorFn(key, values.length + 1, nestedValue);
              if (operatorResult.needsValue) {
                const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : nestedValue;
                if (Array.isArray(transformedValue) && nestedOp === '$elemMatch') {
                  values.push(...transformedValue);
                } else {
                  values.push(transformedValue);
                }
              }
              return operatorResult.sql;
            })
            .join(' AND ');
          return `NOT (${nestedConditions})`;
        }

        if (!FILTER_OPERATORS[operator as OperatorType]) {
          throw new Error(`Invalid operator: ${operator}`);
        }
        const operatorFn = FILTER_OPERATORS[operator as OperatorType]!;
        const operatorResult = operatorFn(key, values.length + 1, operatorValue);
        if (operatorResult.needsValue) {
          const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : operatorValue;
          if (Array.isArray(transformedValue) && operator === '$elemMatch') {
            values.push(...transformedValue);
          } else {
            values.push(transformedValue);
          }
        }
        return operatorResult.sql;
      });
      return conditions.join(' AND ');
    }

    // Single operator case
    const [[operator, operatorValue] = []] = entries;

    // Special handling for nested $not
    if (operator === '$not') {
      const nestedEntries = Object.entries(operatorValue as Record<string, unknown>);
      const conditions = nestedEntries
        .map(([nestedOp, nestedValue]) => {
          if (!FILTER_OPERATORS[nestedOp as OperatorType]) {
            throw new Error(`Invalid operator in $not condition: ${nestedOp}`);
          }
          const operatorFn = FILTER_OPERATORS[nestedOp as OperatorType]!;
          const operatorResult = operatorFn(key, values.length + 1, nestedValue);
          if (operatorResult.needsValue) {
            const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : nestedValue;
            if (Array.isArray(transformedValue) && nestedOp === '$elemMatch') {
              values.push(...transformedValue);
            } else {
              values.push(transformedValue);
            }
          }
          return operatorResult.sql;
        })
        .join(' AND ');

      return `NOT (${conditions})`;
    }
    const operatorFn = FILTER_OPERATORS[operator as OperatorType]!;
    const operatorResult = operatorFn(key, values.length + 1, operatorValue);
    if (operatorResult.needsValue) {
      const transformedValue = operatorResult.transformValue ? operatorResult.transformValue() : operatorValue;
      if (Array.isArray(transformedValue) && operator === '$elemMatch') {
        values.push(...transformedValue);
      } else {
        values.push(transformedValue);
      }
    }
    return operatorResult.sql;
  }

  function handleLogicalOperator(
    key: '$and' | '$or' | '$not' | '$nor',
    value: VectorFilter[],
    parentPath: string,
  ): string {
    if (key === '$not') {
      // For top-level $not
      const entries = Object.entries(value);
      const conditions = entries
        .map(([fieldKey, fieldValue]) => buildCondition(fieldKey, fieldValue, key))
        .join(' AND ');
      return `NOT (${conditions})`;
    }

    // Handle empty conditions
    if (!value || value.length === 0) {
      switch (key) {
        case '$and':
        case '$nor':
          return 'true'; // Empty $and/$nor match everything
        case '$or':
          return 'false'; // Empty $or matches nothing
        default:
          return 'true';
      }
    }

    const joinOperator = key === '$or' || key === '$nor' ? 'OR' : 'AND';
    const conditions = value.map((f: VectorFilter) => {
      const entries = Object.entries(f || {});
      if (entries.length === 0) return '';

      const [firstKey, firstValue] = entries[0] || [];
      if (['$and', '$or', '$not', '$nor'].includes(firstKey as string)) {
        return buildCondition(firstKey as string, firstValue, parentPath);
      }
      return entries.map(([k, v]) => buildCondition(k, v, parentPath)).join(` ${joinOperator} `);
    });

    const joined = conditions.join(` ${joinOperator} `);
    const operatorFn = FILTER_OPERATORS[key]!;
    return operatorFn(joined, 0, value).sql;
  }

  if (!filter) {
    return { sql: '', values };
  }

  const conditions = Object.entries(filter)
    .map(([key, value]) => buildCondition(key, value, ''))
    .filter(Boolean)
    .join(' AND ');

  return { sql: conditions ? `WHERE ${conditions}` : '', values };
}
